import { dcolors, dlog, TObject } from "corexxx";
import { useDAppCommand } from "../../libs/dweb/DAppCommand";
import {
  DPageRootLayout,
  DPageRootLayoutWithDrawer,
} from "../../libs/dweb/DPageRootLayout";
import { convertEpochTo2HrFormat } from "./helper"
import { useMoney } from "../models/Hook";
import { useEffect, useState } from "react";
import { BottomTable, ChainSummary, SymbolChart, TopSymbolSummary } from "./HelperJsx"
import { YearBudget } from "./YearBudgetPage";
import { NetWorthView } from "./NetWorthPage";
import { DNeedsLogin } from "../../libs/dweb/DNeedsLogin";
import { DResponsiveWindow } from "../../libs/dweb/DResponsiveWindow";
import { Button } from "@mui/material";
import { DButton } from "../../libs/dweb/DButton";
import { DCol } from "../../libs/dweb/DCol";
import { DBox } from "../../libs/dweb/DBox";
import { DRow } from "../../libs/dweb/DRow";
import { DDialogSimple } from "../../libs/dweb/DDialogSimple";
import { DText } from "../../libs/dweb/DText";
import { DLayoutTwoCol } from "../../libs/dweb/DLayoutTwoCol";
import { DHook } from "../../libs/common_hooks/DHook";
import { DTwoPaneView } from "../../libs/dweb/DTwoPaneView";
import { DTable } from "../../libs/dweb/DTable";
import { DIconButton } from "../../libs/dweb/DIconButton";
import { DAlert } from "../../libs/dweb/DAlert";
import { LineChart, CartesianGrid, XAxis, YAxis, Legend, Line, Tooltip, ResponsiveContainer, Text, Label, ReferenceLine } from "recharts";
import React from "react";
import { DRefreshButton } from "../../libs/dweb/DRefreshButton";
import { getLastTradingDay, convertToQuote, getFormattedDate, calculateNetDelta, mergeTradingData, generateTradingTimes } from "./helper";
import { DSelectInput } from "../../libs/dweb/DSelectInput";

const SYMBOLS = "AAPL,MSFT,NVDA,AMZN,META,GOOGL,TSLA,AVGO,NFLX"; // Top 10 SPX stocks
const SECTORS = "XLU,XLP,XLV,XLK,XLE,XLF,XLB,XLI,XLY,XLT,VNQ"; // Top 10 SPX stocks

const SECTOR_DESCRIPTIONS: TObject = {
  "XLU": "Utilities.",
  "XLP": "ConsumerStaples",
  "XLV": "Healthcare",
  "XLK": "Technology",
  "XLE": "Energy",
  "XLF": "Financials",
  "XLB": "Materials",
  "XLI": "Industrials",
  "XLY": "ConsumerDisc",
  "XLT": "Telecomm",
  "VNQ": "RealEstate"
};
export function StockAnalysisView() {
  let appCommand = useDAppCommand();
  let [ticker, setTicker] = useState('AAPL');
  let [setting, setSetting] = useState(false)
  let [netDelta, setNetDelta] = React.useState([])
  let [date, setDate] = React.useState('')

  let stock = DHook.useNetwork(`${appCommand.state.simpleStoreEndpoint}/api/utils/schwab/quote?auth_token_=${appCommand.accountState?.auth_token_}&symbols=AAPL,MSFT,NVDA,AMZN,META,GOOGL,TSLA,AVGO,NFLX,$VIX,$SPX`)
  let sector = DHook.useNetwork(`${appCommand.state.simpleStoreEndpoint}/api/utils/schwab/quote?auth_token_=${appCommand.accountState?.auth_token_}&symbols=${SECTORS}`)
  let ticketChainList = DHook.useNetwork(`${appCommand.state.simpleStoreEndpoint}/api/utils/schwab/list_chains?auth_token_=${appCommand.accountState?.auth_token_}&symbol=${ticker}`)
  let tickerChainResp = DHook.useNetwork(`${appCommand.state.simpleStoreEndpoint}/api/utils/schwab/chains?auth_token_=${appCommand.accountState?.auth_token_}&symbol=${ticker}&date=${date}`)
  let tickerHistoryResp = DHook.useNetwork(`${appCommand.state.simpleStoreEndpoint}/api/utils/schwab/pricehistory?auth_token_=${appCommand.accountState?.auth_token_}&symbol=${ticker}&date=${getLastTradingDay()}`)

  React.useEffect(() => {
    if (!ticketChainList.state.data) {
      return;
    }
    dlog.d("loops....")
    setDate(ticketChainList.state.data?.out.expirationList?.[0].expirationDate)
  }, [ticketChainList.state.data])

  return (
    <>
      <DBox
        innerStyle={{ padding: 0 }}
        style={{ padding: 0, background: 'none', boxShadow: 'none' }}>
        <DRow>
          <DCol style={{ background: 'none', flex: 4 }}>
            <DRow center style={{ padding: 0, background: dcolors.pallets.amber100, flex: 0 }}>
              <DSelectInput
                value={ticker}
                style={{ marginLeft: 10, marginRight: 10, }}
                onChange={setTicker}
                options={['AAPL', 'META', 'TSLA', 'MSFT', 'AMZN']}
              />
              {stock.state.data ? <TopSymbolSummary data={stock.state.data.out[ticker]} /> : null}
              <ChainSummary spxChain={tickerChainResp} />
              <DSelectInput
                value={date}
                style={{ marginLeft: 10, marginRight: 10, }}
                onChange={setDate}
                options={ticketChainList.state.data?.out.expirationList?.map((x: any) => x.expirationDate) || []}
              />
              <DRefreshButton onClick={() => {
                stock.api.reload();
                sector.api.reload();
                tickerChainResp.api.reload()
                tickerChainResp.api.reload()
                tickerHistoryResp.api.reload()
              }} />
            </DRow>
            <SymbolChart symbol={ticker} data={tickerHistoryResp.state.data?.out.candles?.map((x: any) => ({ time: convertEpochTo2HrFormat(x.datetime), price: x.close }))} onMouseMove={undefined} />
            <BottomTable spxChain={tickerChainResp} />
          </DCol>

          <DCol style={{ flex: 1, width: '250', paddingLeft: 20 }}>
            <DTable
              style={{ marginBottom: 10 }}
              config={{ title: 'Stock' }}
              data={convertToQuote(stock)}
              columns={[
                { field: 'symbol' },
                { field: 'ltp' },
                { field: 'pchange', format: (x) => (x || 0).toFixed(2) + '%', styleFn: (x: number) => ({ color: x > 0 ? 'green' : 'red' }) },
              ]}
            />

            <DTable
              config={{ title: 'Sector' }}
              data={convertToQuote(sector)}
              columns={[
                { field: 'symbol', format: (x) => SECTOR_DESCRIPTIONS[x] },
                { field: 'ltp' },
                { field: 'pchange', format: (x) => (x || 0).toFixed(2) + '%', styleFn: (x: number) => ({ color: x > 0 ? 'green' : 'red' }) },
              ]}
            />
          </DCol>
        </DRow>
        <DRow style={{ flex: 0 }}>
          <DButton text="Start Scheduler" onClick={() => appCommand.api.getSimpleStore('/api/utils/schwab/net_delta?cmd=start')}></DButton>
        </DRow>
      </DBox >
    </>
  );
}






